import api from '@/components/mixins/baseApi';

/**
 * Подписка на push-уведомления.
 *
 * Договорённость с бэком описана в docs/push-notifications-plan.md:
 * `GET /push/vapid` отдаёт публичный ключ, `POST /push/subscription` сохраняет
 * адрес устройства вместе с ключами шифрования, `DELETE /push/subscription`
 * его убирает — и делает это без авторизации, чтобы отписка работала и с
 * протухшим токеном.
 *
 * Все запросы идут с silentErrors. Общий разбор ответов уводит с 404 на
 * страницу «не найдено», а подписка — дело фоновое и экран за собой тянуть
 * не должна.
 *
 * Флаг остался от времени, когда маршрутов не было. С 4 сентября 2026 они
 * работают — проверка в docs/push-notifications-api-check.md. Выключается,
 * если push у клиента вырезан или бэк отвалился надолго.
 */
export const PUSH_API_READY = true;

/**
 * Ключи модалки с предложением подписаться. Живут на устройстве, а не на
 * мероприятии. Названы с запасом на будущее разделение по категориям:
 * добавится суффикс, переписывать хранилище не придётся.
 */
export const PUSH_ASK_COUNT_KEY = 'push-ask-count';
export const PUSH_ASK_TS_KEY = 'push-ask-ts';
export const PUSH_OPTOUT_KEY = 'push-optout';

/**
 * Человек сам отключил уведомления в настройках. Отдельно от разрешения:
 * разрешение остаётся выданным, отозвать его из кода нельзя, и без этой метки
 * сверка при следующем запуске подписала бы обратно.
 */
export const PUSH_DISABLED_KEY = 'push-disabled';

/** Не чаще раза в три часа. */
export const PUSH_ASK_INTERVAL = 3 * 60 * 60 * 1000;

/** С третьего показа в модалке появляется «Больше не спрашивать». */
export const PUSH_ASK_OPTOUT_FROM = 3;

/**
 * Сброс при выходе из учётной записи: следующий человек на этом устройстве
 * должен получить вопрос заново, а не унаследовать чужой отказ.
 */
export function clearPushAskState() {
  [PUSH_ASK_COUNT_KEY, PUSH_ASK_TS_KEY, PUSH_OPTOUT_KEY, PUSH_DISABLED_KEY]
    .forEach((key) => localStorage.removeItem(key));
}

/**
 * Ключ приходит с бэка строкой base64url, а applicationServerKey принимает
 * байты. Chrome съедает и строку, Firefox — нет.
 */
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(base64);
  const output = new Uint8Array(raw.length);

  for (let i = 0; i < raw.length; i += 1) output[i] = raw.charCodeAt(i);

  return output;
}

/**
 * navigator.serviceWorker.ready не отклоняется никогда: если service worker на
 * этом origin не зарегистрирован, промис висит вечно. Поэтому ждём с
 * ограничением — иначе на нём повисает всё, что его дожидается, включая выход
 * из учётной записи.
 */
const SW_READY_TIMEOUT = 3000;

function serviceWorkerReady() {
  // getRegistration отвечает сразу и отдаёт undefined, когда регистрации нет.
  // ready в этом случае просто висит, поэтому спрашиваем сначала его.
  return navigator.serviceWorker.getRegistration()
    .then((registration) => {
      if (!registration) return null;

      return Promise.race([
        navigator.serviceWorker.ready,
        new Promise((resolve) => { setTimeout(() => resolve(null), SW_READY_TIMEOUT); }),
      ]);
    })
    .catch(() => null);
}

/**
 * Подписка намертво привязана к ключу, с которым её завели. Ключ сменился —
 * старую надо снять, иначе subscribe() бросит InvalidStateError.
 */
function madeWithSameKey(subscription, keyBytes) {
  const current = subscription.options?.applicationServerKey;
  if (!current) return false;

  const currentBytes = new Uint8Array(current);
  if (currentBytes.length !== keyBytes.length) return false;

  return currentBytes.every((byte, index) => byte === keyBytes[index]);
}

export default {
  mixins: [api],

  methods: {
    /**
     * Умеет ли браузер push. На iOS PushManager появляется только у приложения,
     * установленного на домашний экран: в Safari-вкладке подписки не будет.
     */
    pushSupported() {
      return 'serviceWorker' in navigator && 'PushManager' in window;
    },

    /**
     * 'granted' | 'default' | 'denied'. Последнее необратимо из кода: браузер
     * второй раз спросить не даст, снимается только в настройках сайта.
     */
    pushPermission() {
      return this.pushSupported() ? Notification.permission : 'denied';
    },

    /**
     * Спрашивать разрешение можно только из обработчика нажатия — окно жеста
     * живёт секунды и до конца цепочки логина не доживает. Safari и iOS такой
     * запрос просто не покажут.
     */
    requestPushPermission() {
      if (!this.pushSupported()) return Promise.resolve('denied');

      return Notification.requestPermission();
    },

    getVapidKey() {
      if (!PUSH_API_READY) return Promise.resolve(null);

      return this.baseAxios({
        method: 'get',
        url: `${this.$store.getters.apiBasePath}/push/vapid`,
        silentErrors: true,
      })
        .then((data) => data?.results?.key || null)
        .catch(() => null);
    },

    sendPushSubscription(subscription) {
      if (!PUSH_API_READY) return Promise.resolve(false);

      return this.baseAxios({
        method: 'post',
        url: `${this.$store.getters.apiBasePath}/push/subscription`,
        data: subscription.toJSON(),
        silentErrors: true,
      })
        .then(() => true)
        .catch(() => false);
    },

    dropPushSubscription(endpoint) {
      if (!PUSH_API_READY) return Promise.resolve(false);

      return this.baseAxios({
        method: 'delete',
        url: `${this.$store.getters.apiBasePath}/push/subscription`,
        data: { endpoint },
        silentErrors: true,
      })
        .then(() => true)
        .catch(() => false);
    },

    /**
     * Заводит подписку и отдаёт её бэку. Разрешение к этому моменту должно быть
     * уже выдано — запрашивать его отсюда поздно.
     *
     * Этот же метод служит сверкой при запуске приложения: бэк по знакомому
     * endpoint обновит метку, по новому — заведёт запись. Без сверки всё
     * работает ровно до первой ротации подписки браузером, а он меняет их молча.
     */
    subscribePush() {
      if (this.pushPermission() !== 'granted') return Promise.resolve(false);

      // Подписка по воле человека снимает его же прежнее «выключить».
      localStorage.removeItem(PUSH_DISABLED_KEY);

      return Promise.all([this.getVapidKey(), serviceWorkerReady()])
        .then(([key, registration]) => {
          if (!key || !registration) return false;

          const keyBytes = urlBase64ToUint8Array(key);

          return registration.pushManager.getSubscription()
            .then((current) => {
              if (!current) return null;
              if (madeWithSameKey(current, keyBytes)) return current;

              // Ключ сменился. Переподписка проходит молча: разрешение уже
              // выдано, пользователя это не потревожит.
              return current.unsubscribe().then(() => null);
            })
            .then((current) => current || registration.pushManager.subscribe({
              userVisibleOnly: true,
              applicationServerKey: keyBytes,
            }))
            .then((subscription) => this.sendPushSubscription(subscription));
        })
        .catch(() => false);
    },

    /** Сколько раз модалку уже показывали на этом устройстве. */
    pushAskCount() {
      return Number(localStorage.getItem(PUSH_ASK_COUNT_KEY)) || 0;
    },

    /**
     * Показывать ли предложение подписаться.
     *
     * Окно нужно ровно ради одного — спросить разрешение, а спросить его можно
     * только по нажатию. Поэтому единственное состояние, в котором оно уместно,
     * это 'default'. Разрешение уже выдано — сверка при запуске подпишет молча,
     * и спрашивать не о чем; запрещено — браузер второй раз спросить не даст.
     *
     * По наличию подписки не смотрим намеренно: она создаётся той же сверкой в
     * это же мгновение, и проверка выигрывала бы гонку через раз.
     */
    shouldAskPush() {
      if (!this.pushSupported()) return false;
      if (this.pushPermission() !== 'default') return false;
      if (localStorage.getItem(PUSH_OPTOUT_KEY)) return false;
      // Отключил сам — предлагать заново неуместно, для этого есть настройки.
      if (localStorage.getItem(PUSH_DISABLED_KEY)) return false;

      const lastAsked = Number(localStorage.getItem(PUSH_ASK_TS_KEY)) || 0;

      return Date.now() - lastAsked >= PUSH_ASK_INTERVAL;
    },

    /** Отметить показ: считаем разы и запоминаем время. */
    markPushAsked() {
      localStorage.setItem(PUSH_ASK_COUNT_KEY, String(this.pushAskCount() + 1));
      localStorage.setItem(PUSH_ASK_TS_KEY, String(Date.now()));
    },

    /** «Больше не спрашивать». Снимается только выходом из учётной записи. */
    disablePushAsk() {
      localStorage.setItem(PUSH_OPTOUT_KEY, '1');
    },

    /**
     * Сверка при запуске приложения. Бэк по знакомому endpoint обновит метку, по
     * новому — заведёт запись. Без сверки всё работает ровно до первой ротации
     * подписки браузером, а он меняет их молча.
     *
     * От subscribePush отличается одним: уважает выключение в настройках.
     * Разрешение остаётся выданным навсегда, поэтому без этой проверки сверка
     * подписывала бы обратно при каждом запуске.
     */
    syncPushSubscription() {
      if (localStorage.getItem(PUSH_DISABLED_KEY)) return Promise.resolve(false);

      return this.subscribePush();
    },

    /** Текущая подписка браузера или null, если её нет. */
    currentPushSubscription() {
      if (!this.pushSupported()) return Promise.resolve(null);

      return serviceWorkerReady()
        .then((registration) => (registration
          ? registration.pushManager.getSubscription()
          : null))
        .catch(() => null);
    },

    /**
     * Снимает подписку. Вызывать до сброса токена: иначе запрос уйдёт
     * неавторизованным. Endpoint запоминаем заранее — после unsubscribe()
     * читать его уже неоткуда.
     */
    unsubscribePush() {
      localStorage.setItem(PUSH_DISABLED_KEY, '1');

      return this.currentPushSubscription()
        .then((subscription) => {
          if (!subscription) return false;

          const { endpoint } = subscription;

          return subscription.unsubscribe()
            .then(() => this.dropPushSubscription(endpoint));
        })
        .catch(() => false);
    },
  },
};
